/**
 * Global application shell (Block 2.2; nav rebuilt Welle A; Redesign Welle R —
 * Hybrid-Chrome). The top bar carries identity + the scope switch
 * (Privatbereich / Workspace / Gruppe) and the Konto menu; the primary
 * navigation lives in a contextual LEFT SIDEBAR beside the routed view. The
 * board/list/markdown editors render full-screen OUTSIDE this shell (D-04).
 *
 * The scope switch adds „Privatbereich" as a first-class scope entry alongside
 * the workspaces (D-146): it is the personal, workspace-übergreifende Ebene.
 *
 * On mobile the sidebar collapses into the burger panel with the same sections.
 *
 * There is deliberately NO Admin menu item — Decision D-01 excludes it.
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate, useRoute } from "../router";

import { CreateGroupDialog } from "./CreateGroupDialog";
import { CreateWorkspaceDialog } from "./CreateWorkspaceDialog";

import "./ryfe.scss";

import type { WsInvite } from "../api";
import type { ReactNode } from "react";
import type { RouteName } from "../router";

type NavItem = { label: string; path: string; match: RouteName };
type NavGroup = { label: string; items: NavItem[] };

const HOME: NavItem = { label: "Home", path: "/", match: "home" };
// v1.8 Cluster B.5 (D-146) — der Privatbereich: der persönliche, Workspace-
// übergreifende Überblick, getrennt vom einzelnen Workspace (Mandant).
const PRIVATE: NavItem = {
  label: "Privatbereich",
  path: "/private",
  match: "private",
};
// Systemlisten-Überblick (Aufgaben/Prozesse/Projekte/CRM/…) des aktiven Scopes.
const SYSTEMLISTS: NavItem = {
  label: "Systemlisten",
  path: "/systemlists",
  match: "systemlists",
};
// „Für mich freigegeben" ist PERSÖNLICH + workspace-übergreifend (zeigt nur die
// eigenen acl_entries des Nutzers, unabhängig vom aktiven Workspace) — es gehört
// deshalb in den Privatbereich-Kontext, NICHT in die workspace-scoped „Dateien".
const SHARED_WITH_ME: NavItem = {
  label: "Für mich freigegeben",
  path: "/shared-with-me",
  match: "shared-with-me",
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Dateien",
    items: [
      { label: "Meine Dateien", path: "/files", match: "files" },
      // v1.8 AP-5 — neue „Dateien"-Unterpunkte (P21/P13/P23).
      { label: "Meine Favoriten", path: "/favorites", match: "favorites" },
      { label: "Archiv", path: "/archive", match: "role-folder" },
      { label: "Papierkorb", path: "/trash", match: "role-folder" },
      { label: "Zuletzt verwendet", path: "/recent", match: "recent" },
      { label: "Freigaben", path: "/shares", match: "shares" },
      { label: "Meine Bibliothek", path: "/library", match: "library" },
    ],
  },
  {
    label: "KI",
    items: [
      { label: "KI-Chat", path: "/ki-chat", match: "ki-chat" },
      { label: "Lenses", path: "/lenses", match: "lenses" },
      { label: "MCP-Tokens", path: "/mcp-tokens", match: "mcp-tokens" },
    ],
  },
];

// Redesign Welle R — the sidebar tree: „Bereiche" (die drei Direkt-Ziele) plus
// die bisherigen Gruppen „Dateien" und „KI" als Sektionen.
const SIDEBAR_SECTIONS: NavGroup[] = [
  { label: "Bereiche", items: [HOME, PRIVATE, SHARED_WITH_ME, SYSTEMLISTS] },
  ...NAV_GROUPS,
];

// „Konto" (right side): account/settings live here alongside wiki + sign-out.
const ACCOUNT_ITEMS: NavItem[] = [
  { label: "Mein Account", path: "/account", match: "account" },
  { label: "Meine Einstellungen", path: "/settings", match: "settings" },
];

export const RyfeShell = ({ children }: { children: ReactNode }) => {
  const {
    branding,
    user,
    logout,
    workspace,
    regularWorkspaces,
    privateScope,
    enterPrivate,
    setWorkspace,
    groups,
    group,
    setGroup,
    refresh,
  } = useRyfe();
  const route = useRoute();
  // Which desktop dropdown is open (null = none), and the mobile burger state.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [burger, setBurger] = useState(false);
  // Offene WS-Einladungen an die eigene E-Mail (Annehmen/Ablehnen als Banner).
  const [invites, setInvites] = useState<WsInvite[]>([]);
  const loadInvites = () =>
    ryfeApi
      .myInvitations()
      .then((r) => setInvites(r.invitations))
      .catch(() => setInvites([]));
  useEffect(() => {
    void loadInvites();
  }, []);
  const acceptInvite = async (id: string) => {
    try {
      await ryfeApi.acceptInvitation(id);
      await refresh(); // die neu beigetretenen Workspaces laden
      void loadInvites();
    } catch {
      /* Fehler ignorieren — der Banner bleibt, der Nutzer kann erneut klicken */
    }
  };
  const declineInvite = async (id: string) => {
    try {
      await ryfeApi.declineInvitation(id);
    } finally {
      void loadInvites();
    }
  };
  // v1.8 — which scope-switcher popover is open ("ws" | "group" | null) and the
  // Create-Group modal.
  const [scopeMenu, setScopeMenu] = useState<"ws" | "group" | null>(null);
  const [createGroup, setCreateGroup] = useState(false);
  const [createWs, setCreateWs] = useState(false);

  const isWsAdmin = workspace?.role === "owner" || workspace?.role === "admin";
  // Redesign Welle R — der Privatbereich ist als Scope aktiv, sobald der Heim-
  // Workspace aktiv ist (privateScope). Der Button ist ein echter Scope-Switch,
  // nicht nur ein Link auf die /private-Seite.
  const privateActive = privateScope;

  // Close any open dropdown on outside click (the group buttons stopPropagation,
  // so their own clicks never reach here) or on route change.
  useEffect(() => {
    const close = () => {
      setOpenGroup(null);
      setScopeMenu(null);
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);
  useEffect(() => {
    setOpenGroup(null);
    setScopeMenu(null);
    setBurger(false);
  }, [route.name]);

  const go = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };
  const isActive = (m: RouteName) => route.name === m;

  return (
    <div className="ryfe-app">
      <header className="ryfe-topbar">
        <a className="ryfe-topbar__brand" href="/files" onClick={go("/files")}>
          {branding.logo && (
            <img
              className="ryfe-topbar__logo"
              src={branding.logo}
              alt=""
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          {branding.productName}
        </a>

        {/* Redesign Welle R — Hybrid-Chrome: der Scope-Switcher trägt oben den
            Wechsel Privatbereich ⇄ Workspace(s) ⇄ Gruppe. */}
        {workspace && (
          <div className="ryfe-scope" aria-label="Scope">
            {/* Privatbereich als eigener Scope-Eintrag (D-146). */}
            <div className="ryfe-scope__level">
              <button
                className={`ryfe-scope__btn ryfe-scope__btn--private${
                  privateActive ? " ryfe-scope__btn--active" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setScopeMenu(null);
                  // Echter Switch in den Privatbereich (Heim-Workspace); die
                  // /private-Seite bleibt vorerst die Landeansicht.
                  enterPrivate();
                  navigate("/private");
                }}
              >
                Privatbereich
              </button>
            </div>

            <span className="ryfe-scope__sep">·</span>

            <div className="ryfe-scope__level">
              <button
                className="ryfe-scope__btn"
                aria-haspopup="true"
                aria-expanded={scopeMenu === "ws"}
                onClick={(e) => {
                  e.stopPropagation();
                  setScopeMenu((m) => (m === "ws" ? null : "ws"));
                }}
              >
                {/* Im Privatbereich ist KEIN Workspace ausgewählt → Platzhalter. */}
                ▾{" "}
                {privateScope ? (
                  <span className="ryfe-scope__placeholder">
                    Workspace wählen
                  </span>
                ) : (
                  workspace.name
                )}{" "}
                <small>Workspace</small>
              </button>
              {scopeMenu === "ws" && (
                <div className="ryfe-scope__menu" role="menu">
                  {regularWorkspaces.length === 0 && (
                    <span className="ryfe-scope__empty">
                      Noch kein Workspace
                    </span>
                  )}
                  {regularWorkspaces.map((ws) => (
                    <button
                      key={ws.id}
                      role="menuitem"
                      className={`ryfe-scope__item${
                        !privateScope && ws.id === workspace.id
                          ? " ryfe-scope__item--active"
                          : ""
                      }`}
                      onClick={() => {
                        setWorkspace(ws);
                        setScopeMenu(null);
                      }}
                    >
                      {ws.name}
                    </button>
                  ))}
                  {/* v1.8 B.1 (D-146) — neuen Workspace (Mandant) anlegen. */}
                  <button
                    role="menuitem"
                    className="ryfe-scope__item ryfe-scope__item--add"
                    data-testid="ws-add"
                    onClick={() => {
                      setScopeMenu(null);
                      setCreateWs(true);
                    }}
                  >
                    + Workspace
                  </button>
                </div>
              )}
            </div>

            {group && <span className="ryfe-scope__sep">›</span>}

            <div className="ryfe-scope__level">
              <button
                className={`ryfe-scope__btn${
                  group ? " ryfe-scope__btn--group" : " ryfe-scope__btn--empty"
                }`}
                aria-haspopup="true"
                aria-expanded={scopeMenu === "group"}
                onClick={(e) => {
                  e.stopPropagation();
                  setScopeMenu((m) => (m === "group" ? null : "group"));
                }}
              >
                {group ? (
                  <>
                    ▾ {group.name} <small>Gruppe</small>
                  </>
                ) : (
                  <>▾ Gruppe wählen</>
                )}
              </button>
              {scopeMenu === "group" && (
                <div className="ryfe-scope__menu" role="menu">
                  <button
                    role="menuitem"
                    className={`ryfe-scope__item${
                      !group ? " ryfe-scope__item--active" : ""
                    }`}
                    onClick={() => {
                      setGroup(null);
                      setScopeMenu(null);
                      navigate("/");
                    }}
                  >
                    Workspace-Ebene
                  </button>
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      role="menuitem"
                      className={`ryfe-scope__item${
                        group?.id === g.id ? " ryfe-scope__item--active" : ""
                      }`}
                      onClick={() => {
                        setGroup(g);
                        setScopeMenu(null);
                        navigate(`/groups/${g.id}`);
                      }}
                    >
                      {g.name}
                    </button>
                  ))}
                  {groups.length === 0 && (
                    <span className="ryfe-scope__empty">Keine Gruppen</span>
                  )}
                  {isWsAdmin && (
                    <button
                      role="menuitem"
                      className="ryfe-scope__item ryfe-scope__item--add"
                      onClick={() => {
                        setScopeMenu(null);
                        setCreateGroup(true);
                      }}
                    >
                      + Gruppe
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <span className="ryfe-topbar__spacer" />

        {/* Burger toggle — CSS shows it only on narrow screens. */}
        <button
          className="ryfe-burger"
          aria-label="Menü"
          aria-expanded={burger}
          onClick={() => setBurger((b) => !b)}
        >
          ☰
        </button>

        {/* Konto dropdown (right) — account/settings/wiki/sign-out. */}
        <div className="ryfe-navgroup ryfe-navgroup--right">
          <button
            className={`ryfe-navlink ryfe-navgroup__btn${
              ACCOUNT_ITEMS.some((i) => isActive(i.match))
                ? " ryfe-navlink--active"
                : ""
            }`}
            aria-haspopup="true"
            aria-expanded={openGroup === "Konto"}
            onClick={(e) => {
              e.stopPropagation();
              setOpenGroup((o) => (o === "Konto" ? null : "Konto"));
            }}
          >
            Konto ▾
          </button>
          {openGroup === "Konto" && (
            <div
              className="ryfe-navgroup__menu ryfe-navgroup__menu--right"
              role="menu"
            >
              <span className="ryfe-navgroup__email">{user?.email}</span>
              {ACCOUNT_ITEMS.map((i) => (
                <a
                  key={i.path}
                  href={i.path}
                  onClick={go(i.path)}
                  role="menuitem"
                  className={`ryfe-navgroup__item${
                    isActive(i.match) ? " ryfe-navgroup__item--active" : ""
                  }`}
                >
                  {i.label}
                </a>
              ))}
              {/* AP3 — Workspace-Einstellungen: für WS-Admins immer erreichbar
                  (sie betreffen den aktiven Workspace; auch im Privatbereich, wo
                  der Heim-Workspace aktiv ist). */}
              {isWsAdmin && (
                <a
                  href="/workspace-settings"
                  onClick={go("/workspace-settings")}
                  role="menuitem"
                  className={`ryfe-navgroup__item${
                    isActive("workspace-settings")
                      ? " ryfe-navgroup__item--active"
                      : ""
                  }`}
                >
                  Workspace-Einstellungen
                </a>
              )}
              {branding.wikiUrl && (
                <a
                  className="ryfe-navgroup__item"
                  href={branding.wikiUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="menuitem"
                >
                  Help / Wiki
                </a>
              )}
              <button
                className="ryfe-navgroup__item ryfe-navgroup__item--danger"
                onClick={() => logout()}
              >
                Abmelden
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile burger panel — slides down directly under the top bar (before
          the body) and scrolls internally if taller than the viewport. */}
      {burger && (
        <nav className="ryfe-mobilenav" aria-label="Mobile">
          {SIDEBAR_SECTIONS.map((g) => (
            <div key={g.label} className="ryfe-mobilenav__group">
              <div className="ryfe-mobilenav__grouplabel">{g.label}</div>
              {g.items.map((i) => (
                <a
                  key={i.path}
                  href={i.path}
                  onClick={go(i.path)}
                  className={`ryfe-mobilenav__item${
                    isActive(i.match) ? " ryfe-mobilenav__item--active" : ""
                  }`}
                >
                  {i.label}
                </a>
              ))}
            </div>
          ))}
          <div className="ryfe-mobilenav__group">
            <div className="ryfe-mobilenav__grouplabel">Konto</div>
            <span className="ryfe-mobilenav__email">{user?.email}</span>
            {ACCOUNT_ITEMS.map((i) => (
              <a
                key={i.path}
                href={i.path}
                onClick={go(i.path)}
                className={`ryfe-mobilenav__item${
                  isActive(i.match) ? " ryfe-mobilenav__item--active" : ""
                }`}
              >
                {i.label}
              </a>
            ))}
            {branding.wikiUrl && (
              <a
                className="ryfe-mobilenav__item"
                href={branding.wikiUrl}
                target="_blank"
                rel="noreferrer"
              >
                Help / Wiki
              </a>
            )}
            <button
              className="ryfe-mobilenav__item ryfe-mobilenav__item--danger"
              onClick={() => logout()}
            >
              Abmelden
            </button>
          </div>
        </nav>
      )}

      <div className="ryfe-shell__body">
        {/* Redesign Welle R — kontextuelle Sidebar trägt die Primär-Navigation. */}
        <nav className="ryfe-sidebar" aria-label="Primary">
          {SIDEBAR_SECTIONS.map((section) => (
            <div key={section.label} className="ryfe-sidebar__section">
              <div className="ryfe-sidebar__label">{section.label}</div>
              {section.items.map((i) => (
                <a
                  key={i.path}
                  href={i.path}
                  onClick={go(i.path)}
                  className={`ryfe-sidebar__item${
                    isActive(i.match) ? " ryfe-sidebar__item--active" : ""
                  }`}
                >
                  {i.label}
                </a>
              ))}
            </div>
          ))}
        </nav>

        <main className="ryfe-main">
          {invites.length > 0 && (
            <div className="ryfe-invite-banner">
              {invites.map((iv) => (
                <div className="ryfe-invite-banner__row" key={iv.id}>
                  <span className="ryfe-invite-banner__text">
                    Einladung in <strong>{iv.workspace_name}</strong> (als{" "}
                    {iv.role})
                  </span>
                  <span className="ryfe-invite-banner__actions">
                    <button
                      className="ryfe-btn ryfe-btn--primary ryfe-btn--sm"
                      onClick={() => void acceptInvite(iv.id)}
                    >
                      Annehmen
                    </button>
                    <button
                      className="ryfe-btn ryfe-btn--ghost ryfe-btn--sm"
                      onClick={() => void declineInvite(iv.id)}
                    >
                      Ablehnen
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
          {children}
        </main>
      </div>

      {createGroup && (
        <CreateGroupDialog
          onClose={() => setCreateGroup(false)}
          onCreated={(g) => {
            setCreateGroup(false);
            setGroup(g);
            navigate(`/groups/${g.id}`);
          }}
        />
      )}

      {createWs && (
        <CreateWorkspaceDialog
          onClose={() => setCreateWs(false)}
          onCreated={() => {
            setCreateWs(false);
            navigate("/");
          }}
        />
      )}
    </div>
  );
};
